import { createAsyncThunk } from "@reduxjs/toolkit";
import { FetchFromBackoffice, PostBackoffice } from "../../utils/request";
import { SavingReducer } from "../reducers/general";
import { RootState } from "../configureStore";
import { NextWeekSharp } from "@mui/icons-material";

export interface AlertData {
  alertId: string;
  serialId: string;
  eventType: string;
  emailRecipient: string;
}

interface MachineAlertState {
  alertData: AlertData[];
  alertTypes: string[],
  saving: SavingReducer;
  loading: boolean;
  typesLoading: boolean;
  lastError: string | null; 
}

export const initialMachineAlertState: MachineAlertState = {
  alertData: [],
  alertTypes: [],
  saving: "ready",
  loading: false,
  typesLoading: false,
  lastError: null
};

export const fetchAlertData = createAsyncThunk(
  "FETCH_ALERT_DATA",
  async (serialId: string) => {
    const url = `machines/${serialId}/alert-emails`;
    try {
      const response = await FetchFromBackoffice<any[]>(url);
      return response.map((item) => ({
        alertId: item.alertId,
        serialId: serialId,
        eventType: item.event_type,
        emailRecipient: item.emailRecipient,
      })) as AlertData[];
    } catch (error) {
      console.error("Anropet misslyckades", error);
      throw error;
    }
  },
);

export const fetchAlertTypes = createAsyncThunk(
  "FETCH_ALERT_TYPES",
  async () => {
    return await FetchFromBackoffice<string[]>("alerts/event-types")
  }
)

export const updateAlertEmail = createAsyncThunk(
  "UPDATE_ALERT_EMAIL",
  async ({ alert, newEmail }: { alert: AlertData; newEmail: string }) => {
    
    console.log("DATA IN THUNK:", alert); // Syns denna i konsolen?

    //1. Remove old alert (Command: RemoveMachineAlert)
    const removeCommand = {
      SerialNumber: alert.serialId,
      Email: alert.emailRecipient,
      AlertType: alert.eventType
    } 
    await PostBackoffice("cmd/alerts/remove-machine-alert", removeCommand); 

    //2. Add the new alert (Command: AddMachineEmailAlert)
    const addCommand = {
      SerialNumber: alert.serialId,
      Email: newEmail,
      AlertType: alert.eventType
    }
    await PostBackoffice("cmd/alerts/addemail-to-machine", addCommand)

    return { alertId: alert.alertId, newEmail };
  },
);

export const deleteAlert = createAsyncThunk(
  "DELETE_ALERT",
  async (alert: AlertData) => {
    console.log("DATA IN THUNK:", alert); // Syns denna i konsolen?

    //map data to fit what Backoffice want
    const command = {
      SerialNumber: alert.serialId,
      Email: alert.emailRecipient,
      AlertType: alert.eventType
    }
    await PostBackoffice("cmd/alerts/remove-machine-alert", command)
    return { alertId: alert.alertId };
  },
);

export const addAlert = createAsyncThunk(
  "ADD_ALERT_AND_EMAIL",
  async (newAlert: Omit<AlertData, "alertId">, {getState, rejectWithValue}) => {

    //Get entire state to check if combination of type and email alrady exist
    const state = getState() as RootState; 
    const currentAlerts = state.machineAlert.alertData; 

    //Fail fast: Check for duplicates
    const isDuplicate = currentAlerts.some(
      (existing) =>
        existing.eventType.toLowerCase() === newAlert.eventType.toLowerCase() &&
        existing.emailRecipient.toLowerCase() === newAlert.emailRecipient.toLowerCase() 
    ); 

    if (isDuplicate){
      return rejectWithValue("This email is already registered for this alert type.")
    }

    try {
    const command = {
      SerialNumber: newAlert.serialId,
      Email: newAlert.emailRecipient,
      AlertType: newAlert.eventType
    }
    const response = await PostBackoffice<any>("cmd/alerts/addemail-to-machine", command);
    return {
      ...newAlert, 
      alertId: response?.alertId || response?.id || Math.random().toString()
    } as AlertData; 
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to save alert");
    }
  },
);